import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Descriptions, Form, Input, Modal, Select, Switch, Tag, Typography, message } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import { hasResource } from 'utils/resourceAuth';
import { ResourceCode } from 'constants/resourceCodes';
import { GET_HUB_LIST, GetHubListResponse } from 'pages/wms/bags/graphql/queries/getHubList';
import {
  MARK_STATIONS_UNREACHABLE,
  MarkStationsUnreachableReport,
  MarkStationsUnreachableResponse,
  MarkStationsUnreachableVariables,
  ROLLBACK_MARK_STATIONS_UNREACHABLE,
  RollbackMarkStationsUnreachableResponse,
  RollbackMarkStationsUnreachableVariables,
  parseIdList,
} from './graphql/opsToolPickupStationGraphql';

/** 后端单次上限（PickupGoliveImportMutation.MAX_UNREACHABLE_IDS） */
const MAX_IDS = 5000;

/** 预演参数指纹：执行按钮只在「最近一次预演」与当前表单完全一致时可点 */
const fingerprint = (hubId: number | undefined, ids: number[], suppress: boolean) =>
  JSON.stringify([hubId, [...ids].sort((a, b) => a - b), suppress]);

const ReportView: React.FC<{ title: string; report: MarkStationsUnreachableReport; rollback?: boolean }> = ({
  title,
  report,
  rollback,
}) => (
  <Descriptions className="mt-4" bordered size="small" column={2} title={title}>
    <Descriptions.Item label="模式">
      {report.dryRun ? <Tag color="blue">预演（未写库）</Tag> : <Tag color="red">已执行</Tag>}
    </Descriptions.Item>
    <Descriptions.Item label="回滚批次号">
      {report.batchNo ? <Typography.Text copyable>{report.batchNo}</Typography.Text> : '-'}
    </Descriptions.Item>
    {rollback ? (
      <>
        <Descriptions.Item label="还原建联状态的站点数">{report.linkStatusRestored}</Descriptions.Item>
        <Descriptions.Item label="还原的审核池条目数">{report.poolRestored}</Descriptions.Item>
        <Descriptions.Item label="恢复的预约单数">{report.appointmentRestored}</Descriptions.Item>
      </>
    ) : (
      <>
        <Descriptions.Item label="去重后请求站点数">{report.requested}</Descriptions.Item>
        <Descriptions.Item label="输入重复 id 数">{report.duplicatedInput}</Descriptions.Item>
        <Descriptions.Item label="查不到或不属于本 Hub" span={2}>
          {report.notFoundOrOtherHub > 0 ? (
            <span>
              <Tag color="red">{report.notFoundOrOtherHub}</Tag>
              {report.notFoundOrOtherHubSample.length > 0 && `样例（前 50）：${report.notFoundOrOtherHubSample.join(', ')}`}
            </span>
          ) : (
            0
          )}
        </Descriptions.Item>
        <Descriptions.Item label="已退役跳过">{report.retiredSkipped}</Descriptions.Item>
        <Descriptions.Item label="已是确认无法建联（跳过）">{report.alreadyUnreachable}</Descriptions.Item>
        <Descriptions.Item label={report.dryRun ? '将置为确认无法建联' : '已置为确认无法建联'}>
          {report.linkStatusUpdated}
        </Descriptions.Item>
        <Descriptions.Item label="状态漂移跳过">{report.skippedByStateDrift}</Descriptions.Item>
        <Descriptions.Item label="审核池命中条目">{report.poolMatched}</Descriptions.Item>
        <Descriptions.Item label="审核池已标记处理">{report.poolMarked}</Descriptions.Item>
        <Descriptions.Item label="审核池部分跳过">{report.poolSkippedPartial}</Descriptions.Item>
        <Descriptions.Item label="审核池已删除跳过">{report.poolSkippedDeleted}</Descriptions.Item>
        <Descriptions.Item label="命中的存量预约单">{report.appointmentTargets}</Descriptions.Item>
        <Descriptions.Item label="已抑制的存量预约单">{report.appointmentSuppressed}</Descriptions.Item>
        <Descriptions.Item label="非已创建状态（不处理）" span={2}>
          {report.appointmentExcludedByStatus.length > 0 ? report.appointmentExcludedByStatus.join('，') : '-'}
        </Descriptions.Item>
      </>
    )}
    <Descriptions.Item label="摘要" span={2}>
      <Typography.Paragraph className="!mb-0 whitespace-pre-wrap">{report.summary}</Typography.Paragraph>
    </Descriptions.Item>
  </Descriptions>
);

const MarkStationsUnreachablePage: React.FC = () => {
  const canOperate = hasResource([ResourceCode.menu.OPS_TOOL_PICKUP]);
  const [form] = Form.useForm();
  const hubId: number | undefined = Form.useWatch('hubId', form);
  const idsText: string | undefined = Form.useWatch('ids', form);
  const suppressAppointments: boolean = Form.useWatch('suppressAppointments', form) ?? true;
  const parsed = useMemo(() => parseIdList(idsText), [idsText]);
  const currentFingerprint = fingerprint(hubId, parsed.ids, suppressAppointments);

  const [report, setReport] = useState<MarkStationsUnreachableReport | null>(null);
  const [dryRunFingerprint, setDryRunFingerprint] = useState<string | null>(null);

  const [rollbackBatchNo, setRollbackBatchNo] = useState('');
  const [rollbackReport, setRollbackReport] = useState<MarkStationsUnreachableReport | null>(null);
  const [rollbackDryRunBatchNo, setRollbackDryRunBatchNo] = useState<string | null>(null);

  const { data: hubData, loading: hubLoading } = useQuery<GetHubListResponse>(GET_HUB_LIST);
  const hubOptions = useMemo(
    () =>
      (hubData?.hubList ?? [])
        .filter((hub) => hub.sortingCode)
        .map((hub) => ({ label: `${hub.sortingCode} - ${hub.name}`, value: Number(hub.id) })),
    [hubData],
  );

  const [mark, { loading }] = useMutation<MarkStationsUnreachableResponse, MarkStationsUnreachableVariables>(
    MARK_STATIONS_UNREACHABLE,
  );
  const [rollback, { loading: rollbackLoading }] = useMutation<
    RollbackMarkStationsUnreachableResponse,
    RollbackMarkStationsUnreachableVariables
  >(ROLLBACK_MARK_STATIONS_UNREACHABLE);

  const idsError =
    parsed.invalid.length > 0
      ? `无法识别的内容：${parsed.invalid.slice(0, 10).join(', ')}`
      : parsed.ids.length > MAX_IDS
        ? `最多 ${MAX_IDS} 个站点，当前 ${parsed.ids.length} 个，请分批`
        : null;
  const inputReady = canOperate && hubId != null && parsed.ids.length > 0 && !idsError;
  const executeReady = inputReady && dryRunFingerprint === currentFingerprint;

  const run = async (dryRun: boolean) => {
    if (!canOperate) {
      message.error('无操作权限');
      return;
    }
    try {
      const { data } = await mark({
        variables: { hubId: hubId!, cooperationWarehouseIds: parsed.ids, dryRun, suppressAppointments },
      });
      const payload = data?.markStationsUnreachable ?? null;
      setReport(payload);
      if (dryRun) {
        setDryRunFingerprint(currentFingerprint);
        message.success('预演完成，请核对统计后再执行');
      } else {
        // 执行后清空预演指纹，防止同一份名单被连点重复执行（后端幂等，但会生成新的批次号）
        setDryRunFingerprint(null);
        if (payload?.batchNo) setRollbackBatchNo(payload.batchNo);
        message.success('已执行');
      }
    } catch (e: any) {
      if (dryRun) {
        message.error(e?.message ?? '预演失败');
        return;
      }
      // 真跑报错 ≠ 没执行：名单大时会撞网关超时、响应丢失，而 pickup-service 已在后台跑完。
      // 清掉预演指纹强制重新预演，避免照着旧预演连点出第二个批次号（第一个批次号页面拿不到）
      setDryRunFingerprint(null);
      Modal.warning({
        title: '执行结果未知，请勿重复执行',
        content: (
          <div>
            <p>错误信息：{e?.message ?? '未知错误'}</p>
            <p>可能已在后台执行完成（名单较大时接口会超时，但数据已写入）。</p>
            <p>
              请先重新「预演」：若「已是确认无法建联」接近名单数，说明已执行；需要回滚时请科技侧在 pickup-service 日志
              flow <code>MarkStationsUnreachable</code>、step <code>BatchNoAssigned</code> 按 Hub 查到批次号。
            </p>
          </div>
        ),
      });
    }
  };

  const handleExecute = () => {
    if (!report || !report.dryRun) return;
    Modal.confirm({
      title: '确认执行「确认无法建联」',
      content: (
        <div>
          <p>Hub：{hubOptions.find((o) => o.value === hubId)?.label ?? hubId}</p>
          <p>将置为确认无法建联的站点：{report.linkStatusUpdated} 个</p>
          <p>将标记已处理的审核池条目：{report.poolMatched} 条</p>
          {suppressAppointments && <p>将抑制（删除）的已创建预约单：{report.appointmentTargets} 单</p>}
          {report.notFoundOrOtherHub > 0 && (
            <p className="text-red-500">有 {report.notFoundOrOtherHub} 个 id 查不到或不属于本 Hub，将被忽略</p>
          )}
          <p>执行后可凭批次号整批回滚。</p>
        </div>
      ),
      okText: '确认执行',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: () => run(false),
    });
  };

  const runRollback = async (dryRun: boolean) => {
    if (!canOperate) {
      message.error('无操作权限');
      return;
    }
    const batchNo = rollbackBatchNo.trim();
    try {
      const { data } = await rollback({ variables: { batchNo, dryRun } });
      setRollbackReport(data?.rollbackMarkStationsUnreachable ?? null);
      setRollbackDryRunBatchNo(dryRun ? batchNo : null);
      message.success(dryRun ? '回滚预演完成' : '已回滚');
    } catch (e: any) {
      if (dryRun) {
        message.error(e?.message ?? '回滚预演失败');
        return;
      }
      // 同真跑：回滚报错也可能已在后台完成，强制重新预演回滚再决定
      setRollbackDryRunBatchNo(null);
      Modal.warning({
        title: '回滚结果未知，请勿重复回滚',
        content: (
          <div>
            <p>错误信息：{e?.message ?? '未知错误'}</p>
            <p>可能已在后台回滚完成。请先重新「预演回滚」，确认还原数后再决定是否执行。</p>
          </div>
        ),
      });
    }
  };

  const handleRollback = () => {
    if (!rollbackReport) return;
    Modal.confirm({
      title: '确认回滚',
      content: (
        <div>
          <p>批次号：{rollbackBatchNo.trim()}</p>
          <p>将还原建联状态的站点：{rollbackReport.linkStatusRestored} 个</p>
          <p>将还原的审核池条目：{rollbackReport.poolRestored} 条</p>
          <p>将恢复的预约单：{rollbackReport.appointmentRestored} 单</p>
        </div>
      ),
      okText: '确认回滚',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: () => runRollback(false),
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <Card title="确认无法建联">
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message="作用（按顺序执行）"
          description={
            <div>
              ① 名单内站点建联状态置为「确认无法建联」，并记建联状态变更日志；
              <br />② 这些站点名下的审核池待处理条目标记为已处理，从待办列表消失；
              <br />③ 开启「同时抑制存量预约单」时，这些站点名下「已创建」状态的预约单被删除。
              <br />
              站点的关联地址不动。必须先预演并核对统计，执行按钮只在预演参数与当前输入完全一致时可用。
            </div>
          }
        />
        <Form form={form} layout="vertical" initialValues={{ suppressAppointments: true }}>
          <Form.Item name="hubId" label="Hub" rules={[{ required: true, message: '请选择 Hub' }]}>
            <Select
              showSearch
              allowClear
              loading={hubLoading}
              options={hubOptions}
              optionFilterProp="label"
              placeholder="选择站点所属 Hub"
              className="!w-80"
            />
          </Form.Item>
          <Form.Item
            name="ids"
            label="站点 id"
            validateStatus={idsError ? 'error' : undefined}
            help={
              idsError ??
              (parsed.ids.length > 0
                ? `已识别 ${parsed.ids.length} 个站点${parsed.duplicated > 0 ? `（重复 ${parsed.duplicated} 个已去重）` : ''}`
                : `逗号、空格或换行分隔，最多 ${MAX_IDS} 个`)
            }
          >
            <Input.TextArea rows={6} placeholder="10261, 8626, 12107" />
          </Form.Item>
          <Form.Item name="suppressAppointments" label="同时抑制存量预约单" valuePropName="checked">
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>
          <div className="flex gap-2">
            <Button loading={loading} disabled={!inputReady} onClick={() => run(true)}>
              预演
            </Button>
            <Button type="primary" danger loading={loading} disabled={!executeReady} onClick={handleExecute}>
              执行
            </Button>
          </div>
        </Form>
        {report && <ReportView title={report.dryRun ? '预演结果' : '执行结果'} report={report} />}
      </Card>

      <Card title="按批次号回滚">
        <div className="flex gap-2">
          <Input
            className="!w-96"
            placeholder="执行结果中的回滚批次号"
            value={rollbackBatchNo}
            onChange={(e) => {
              setRollbackBatchNo(e.target.value);
              setRollbackDryRunBatchNo(null);
            }}
          />
          <Button
            loading={rollbackLoading}
            disabled={!canOperate || !rollbackBatchNo.trim()}
            onClick={() => runRollback(true)}
          >
            预演回滚
          </Button>
          <Button
            type="primary"
            danger
            loading={rollbackLoading}
            disabled={!canOperate || !rollbackBatchNo.trim() || rollbackDryRunBatchNo !== rollbackBatchNo.trim()}
            onClick={handleRollback}
          >
            执行回滚
          </Button>
        </div>
        {rollbackReport && (
          <ReportView title={rollbackReport.dryRun ? '回滚预演结果' : '回滚结果'} report={rollbackReport} rollback />
        )}
      </Card>
    </div>
  );
};

export default MarkStationsUnreachablePage;
